import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import Button from "shared/Button/Button";
import { ClipLoader } from "react-spinners";
import { IButtonSecondaryProps } from "@/Interfaces/interface.buttons";

const MotionButton = motion.create(Button);
const MotionAnchor = motion.a;

const sizeMap = {
  xs: {
    padding: "px-2 py-1",
    text: "text-xs",
    iconSize: "w-3 h-3",
  },
  sm: {
    padding: "px-3 py-1.5",
    text: "text-sm",
    iconSize: "w-4 h-4",
  },
  md: {
    padding: "px-4 py-2",
    text: "text-base",
    iconSize: "w-5 h-5",
  },
  lg: {
    padding: "px-5 py-2.5",
    text: "text-lg",
    iconSize: "w-6 h-6",
  },
  xl: {
    padding: "px-6 py-3",
    text: "text-xl",
    iconSize: "w-7 h-7",
  },
};

const borderRadiusMap = {
  none: "rounded-none",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  full: "rounded-full",
};

const variantStylesMap = {
  basic: {
    enabled:
      "bg-indigo-500 text-white font-semibold hover:bg-indigo-600 dark:bg-indigo-500 dark:hover:bg-indigo-600",
    disabled:
      "bg-gray-300 text-gray-600 font-semibold cursor-not-allowed pointer-events-none dark:bg-gray-600 dark:text-gray-400",
  },
  cancel: {
    enabled:
      "bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700",
    disabled:
      "bg-red-300 text-red-100 cursor-not-allowed pointer-events-none dark:bg-red-800 dark:text-red-400",
  },
  info: {
    enabled:
      "bg-white text-indigo-500 hover:bg-white dark:bg-blue-600 dark:hover:bg-blue-700",
    disabled:
      "bg-white text-indigo-300 cursor-not-allowed pointer-events-none dark:bg-blue-800 dark:text-blue-400",
  },
  disabled: {
    enabled:
      "bg-gray-300 text-gray-500 cursor-not-allowed pointer-events-none dark:bg-gray-600 dark:text-gray-400",
    disabled:
      "bg-gray-300 text-gray-500 cursor-not-allowed pointer-events-none dark:bg-gray-600 dark:text-gray-400",
  },
};

const ButtonSecondary: React.FC<IButtonSecondaryProps> = ({
  className = "",
  disabled,
  loading = false,
  icon,
  text,
  children,
  href,
  size,
  borderRadius,
  variant = "basic",
  ...args
}) => {
  const isDisabled = disabled || loading || variant === "disabled";
  const contentRef = useRef<HTMLSpanElement>(null);
  const [contentWidth, setContentWidth] = useState<number | null>(null);

  useEffect(() => {
    if (!loading && contentRef.current) {
      const width = contentRef.current.offsetWidth;
      setContentWidth(width);
    }
  }, [loading, text, children, icon]);

  // Removed w-full from baseStyles to allow custom width classes
  const baseStyles =
    "inline-flex flex-wrap items-center justify-center rounded-md font-medium transition-all duration-200 overflow-hidden relative group focus:outline-none text-sm sm:text-base flex-shrink-0";

  const variantStyles = variantStylesMap[variant] || variantStylesMap.basic;
  const currentVariantStyles = isDisabled
    ? variantStyles.disabled
    : variantStyles.enabled;

  const sizeStyles = sizeMap[size || "sm"] || sizeMap.md;
  const radiusStyles =
    borderRadiusMap[borderRadius || "md"] || borderRadiusMap.md;

  const combinedClassName = [
    baseStyles,
    currentVariantStyles,
    sizeStyles.padding,
    sizeStyles.text,
    radiusStyles,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <span
      ref={contentRef}
      className="relative z-10 flex items-center gap-1 sm:gap-2 justify-center"
      style={
        loading && contentWidth ? { width: `${contentWidth}px` } : undefined
      }
    >
      {loading ? (
        <ClipLoader loading={true} size={20} color="blue" />
      ) : (
        <>
          {icon && (
            <span className="flex-shrink-0 inline-flex items-center">
              {icon}
            </span>
          )}
          {(text || children) && (
            <span className="flex-shrink text-ellipsis inline-flex items-center gap-1">
              {text || children}
            </span>
          )}
        </>
      )}
    </span>
  );

  if (href) {
    return (
      <MotionAnchor
        href={href}
        className={combinedClassName}
        whileTap={!isDisabled ? { scale: 0.95 } : {}}
        aria-disabled={isDisabled}
        tabIndex={isDisabled ? -1 : undefined}
        onClick={(e) => {
          if (isDisabled) e.preventDefault();
        }}
        {...(args as any)}
      >
        {content}
      </MotionAnchor>
    );
  }

  return (
    <MotionButton
      className={combinedClassName}
      disabled={isDisabled}
      whileTap={!isDisabled ? { scale: 0.95 } : {}}
      {...args}
    >
      {content}
    </MotionButton>
  );
};

export default ButtonSecondary;
